import React from 'react';
import { Check } from 'lucide-react';
import { MaxWidthContainer, SectionTitle, Button } from '../styles/components';
import styled, { css } from 'styled-components';
import { pricingPlans } from '../../../../constants/pricingPlans';
import { useNavigate } from 'react-router-dom';

interface CardProps {
    $highlighted?: boolean;
}

export const PricingSection = styled.section`
  padding: ${({ theme }) => theme.spacing[20]} ${({ theme }) => theme.spacing[4]};
  background-color: ${({ theme }) => theme.colors.gray[100]};
`;

export const PricingGrid = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.spacing[8]};
    margin: 0 auto;

    /* Mobile: horizontal scroll */
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        overflow-x: auto;
        padding-bottom: ${({ theme }) => theme.spacing[4]};
        scroll-snap-type: x mandatory;
        -webkit-overflow-scrolling: touch;

        /* Hide scrollbar for Chrome, Safari and Opera */
        &::-webkit-scrollbar {
            display: none;
        }

        /* Hide scrollbar for IE, Edge and Firefox */
        -ms-overflow-style: none;
        scrollbar-width: none;
    }

    /* Desktop: fit all cards horizontally */
    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
        justify-content: center;
        flex-wrap: nowrap;
    }
`;

export const PricingCardWrapper = styled.div<CardProps>`
    flex: 0 0 auto;

    /* Mobile: each card takes most of the viewport width */
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        width: 85vw;
        scroll-snap-align: center;
    }

    /* Desktop: cards fit naturally */
    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
        width: auto;
        min-width: 400px;
        max-width: 550px;
    }
`;

export const Card = styled.div<CardProps>`
    border-radius: ${({ theme }) => theme.borderRadius.xl};
    padding: ${({ theme }) => theme.spacing[8]};
    height: 100%;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    transition: box-shadow 0.3s ease;
    display: flex;
    flex-direction: column;

    ${({ $highlighted, theme }) => $highlighted ? css`
    background-color: ${theme.colors.primary[500]};
    color: ${theme.colors.white};
  ` : css`
    background-color: ${theme.colors.white};
  `}

    &:hover {
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    }
`;

export const PlanName = styled.h3`
    font-size: 1.5rem;
    font-weight: bold;
    text-align: center;
    margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

export const PriceWrapper = styled.div`
    text-align: center;
`;

export const Price = styled.span`
    font-size: 2.25rem;
    font-weight: bold;
`;

export const PriceUnit = styled.span<CardProps>`
  color: ${({ $highlighted, theme }) =>
    $highlighted ? theme.colors.primary[100] : theme.colors.gray[500]};
`;

export const PlanDescription = styled.p<CardProps>`
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing[1]};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  font-size: 0.875rem;
  color: ${({ $highlighted, theme }) =>
    $highlighted ? theme.colors.primary[100] : theme.colors.gray[500]};
`;

export const FeatureList = styled.ul`
  margin-bottom: ${({ theme }) => theme.spacing[8]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
  flex-grow: 1;
`;

export const FeatureItem = styled.li<CardProps>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  
  svg {
    color: ${({ $highlighted, theme }) =>
    $highlighted ? theme.colors.white : theme.colors.primary[500]};
    flex-shrink: 0;
  }
`;

export function PricingPlans() {
    const navigate = useNavigate();

    return (
        <PricingSection id="pricing">
            <MaxWidthContainer>
                <SectionTitle>Simple, Transparent Pricing</SectionTitle>
                {/*<SectionDescription>*/}
                {/*    Choose the plan that fits your needs.*/}
                {/*</SectionDescription>*/}
                <PricingGrid>
                    {pricingPlans.map((plan) => (
                        <PricingCardWrapper key={plan.id} $highlighted={plan.highlighted}>
                            <Card $highlighted={plan.highlighted}>
                                <PlanName>{plan.name}</PlanName>
                                <PriceWrapper>
                                    <Price>${plan.price}</Price>
                                    <PriceUnit $highlighted={plan.highlighted}>{plan.priceUnit}</PriceUnit>
                                </PriceWrapper>
                                <PlanDescription $highlighted={plan.highlighted}>
                                    {plan.description}
                                </PlanDescription>
                                <FeatureList>
                                    {plan.features.map((feature) => (
                                        <FeatureItem key={feature} $highlighted={plan.highlighted}>
                                            <Check size={20} />
                                            <span>{feature}</span>
                                        </FeatureItem>
                                    ))}
                                </FeatureList>
                                <Button
                                    variant={plan.highlighted ? 'white' : 'primary'}
                                    fullWidth
                                    onClick={() => navigate('/login')}
                                >
                                    {plan.buttonText}
                                </Button>
                            </Card>
                        </PricingCardWrapper>
                    ))}
                </PricingGrid>
            </MaxWidthContainer>
        </PricingSection>
    );
}
